import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

interface CreateInviteParams {
  companyId: string;
  email: string;
  role?: Tables<"company_access_requests">["role"];
}

interface AcceptInvitationResult {
  company_id: string;
  created_at: string;
  email: string | null;
  expires_at: string | null;
  id: string;
  invite_token: string | null;
  notes: string | null;
  role: "admin" | "manager" | "member" | "sales";
  status: string;
  updated_at: string;
  updated_by: string | null;
  user_id: string | null;
  alreadyMember?: boolean;
}

export const createInvitation = async ({ companyId, email, role = 'member' }: CreateInviteParams) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // 1) Block duplicates: pending invite for same company + email (case-insensitive)
  const { data: existing, error: existingError } = await supabase
    .from('company_access_requests')
    .select('id, status')
    .eq('company_id', companyId)
    .is('user_id', null)
    .or('status.is.null,status.eq.pending')
    .ilike('email', email) // case-insensitive equality
    .limit(1);

  if (existingError) throw existingError;
  if (existing && existing.length > 0) {
    return {
      invite: null,
      emailStatus: 'skipped' as const,
      duplicate: true as const,
      emailErrorMessage: 'An invitation for this email is already pending for this company.'
    };
  }

  // 2) Create invitation
  const { data, error } = await supabase
    .from('company_access_requests')
    .insert({
      company_id: companyId,
      email: email,
      updated_by: user.id,
      role: role,
      status: 'pending',
      invite_token: crypto.randomUUID(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      user_id: null
    })
    .select()
    .single();

  if (error) throw error;

  // 3) Send the invite email
  const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-company-invite', {
    body: {
      email: email,
      companyId: companyId,
      role: role,
      inviteToken: data.invite_token,
    }
  });

  if (emailError) {
    console.error('Failed to send invite email:', emailError);

    try {
      const { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } = await import('@supabase/supabase-js');

      if (emailError instanceof FunctionsHttpError) {
        let payload: any = null;
        try {
          payload = await emailError.context.json();
        } catch {}

        if (payload?.error === 'resend_domain_not_verified') {
          return {
            invite: data,
            emailStatus: 'failed' as const,
            emailErrorCode: 'resend_domain_not_verified',
            emailErrorMessage: payload?.message || 'Email domain not verified. Please verify your domain in Admin settings.',
          };
        }

        return {
          invite: data,
          emailStatus: 'failed' as const,
          emailErrorMessage: payload?.message || emailError.message,
        };
      }

      if (emailError instanceof FunctionsRelayError || emailError instanceof FunctionsFetchError) {
        return {
          invite: data,
          emailStatus: 'failed' as const,
          emailErrorMessage: emailError.message,
        };
      }
    } catch {}

    return {
      invite: data,
      emailStatus: 'failed' as const,
      emailErrorMessage: emailError.message || 'Edge Function returned a non-2xx status code',
    };
  }

  return {
    invite: data,
    emailStatus: 'sent' as const
  };

};

export const acceptInvitation = async (inviteToken: string): Promise<AcceptInvitationResult> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // STEP 1: Fetch the invitation details
  const { data: invitation, error: fetchError } = await supabase
    .from('company_access_requests')
    .select('*')
    .eq('invite_token', inviteToken)
    .eq('status', 'pending')
    .single();

  if (fetchError || !invitation) {
    throw new Error('Invitation not found or already used');
  }

  // STEP 2: Check if user already has an access request for this company
  const { data: existingRequests, error: existingError } = await supabase
    .from('company_access_requests')
    .select('*')
    .eq('user_id', user.id)
    .eq('company_id', invitation.company_id);

  if (existingError) throw existingError;

  // STEP 3: Handle existing requests
  if (existingRequests && existingRequests.length > 0) {
    const approvedRequest = existingRequests.find(r => r.status === 'approved');
    
    if (approvedRequest) {
      // User already has access - just delete the pending invitation
      await supabase
        .from('company_access_requests')
        .delete()
        .eq('invite_token', inviteToken);
      
      // Make sure profile is updated
      await supabase
        .from('profiles')
        .update({ company_id: invitation.company_id })
        .eq('id', user.id);
      
      return { ...invitation, status: 'approved', alreadyMember: true };
    }
  }

  // STEP 4: SECURITY - Verify logged-in user's email matches invitation email
  const normalizedUserEmail = user.email?.toLowerCase().trim();
  const normalizedInviteEmail = invitation.email?.toLowerCase().trim();
  
  if (normalizedUserEmail !== normalizedInviteEmail) {
    const error = new Error(
      `Email mismatch: This invitation was sent to ${invitation.email}. You are logged in as ${user.email}. Please log out and sign in with the invited email address.`
    );
    error.name = 'EmailMismatchError';
    throw error;
  }

  // STEP 5: No conflicts - proceed with accepting the invitation
  const { data, error } = await supabase
    .from('company_access_requests')
    .update({
      status: 'approved',
      user_id: user.id
    })
    .eq('invite_token', inviteToken)
    .eq('status', 'pending')
    .select()
    .single();

  if (error) {
    // If we still get a duplicate key error, it means a race condition occurred
    // Check if user now has access and return success
    if (error.code === '23505') { // Unique constraint violation
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();
      
      if (profile?.company_id === invitation.company_id) {
        return { ...invitation, status: 'approved', alreadyMember: true };
      }
    }
    throw error;
  }

  // STEP 6: Update user's company_id in their profile
  const { error: profileError } = await supabase
    .from('profiles')
    .update({ company_id: data.company_id })
    .eq('id', user.id);

  if (profileError) {
    console.error('Failed to update user profile:', profileError);
  }

  return data;
};

export const resendInvitation = async (invitationId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const newToken = crypto.randomUUID();
  const newExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('company_access_requests')
    .update({
      invite_token: newToken,
      expires_at: newExpiry,
      updated_by: user.id,
    })
    .eq('id', invitationId)
    .select()
    .single();

  if (error) throw error;

  const { error: emailError } = await supabase.functions.invoke('send-company-invite', {
    body: {
      email: data.email,
      companyId: data.company_id,
      role: data.role,
      inviteToken: newToken,
    }
  });

  if (emailError) {
    console.error('Failed to resend invite email:', emailError);
    return { invite: data, emailStatus: 'failed' as const, emailErrorMessage: emailError.message };
  }

  return { invite: data, emailStatus: 'sent' as const };
};

export const deleteInvitation = async (invitationId: string) => {
  const { error } = await supabase
    .from('company_access_requests')
    .delete()
    .eq('id', invitationId);
  if (error) throw error;
};

export const getCompanyInvitations = async (companyId: string) => {
  const { data, error } = await supabase
    .from('company_access_requests')
    .select('*')
    .eq('company_id', companyId)
    .or('status.is.null,status.eq.pending')
    .is('user_id', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};
